import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGacha } from "@/hooks/useGacha";
import { useAuth } from "@/components/AuthContext";
import { calculateProgress, getProgressColor, getProgressLabel, isSoldOut } from "@/utils/progressUtils";
import LotteryAnimation from "@/components/LotteryAnimation";
import ResponsiveImage, { ImageVariants } from "@/components/ResponsiveImage";
import PhoneVerificationDialog from "@/components/PhoneVerificationDialog";

type EventCardProps = {
  id: number;
  uid: string;
  siteId: string;
  siteSlug?: string;
  name: string;
  description?: string;
  imageUrl: string;
  imageVariants?: ImageVariants;
  lotteryPoolsAvailableCount: number | null;
  lotteryPoolsCount: number;
  status?: "draft" | "ready" | "published" | "active" | "finished" | "cancelled" | "archived";
  status_label?: string;
  lotteryAnimationUrl?: string;
  prizeImages?: string[];
  prizeImageVariantUrls?: Array<ImageVariants | null>;
  gachaCost?: number;
  useProductStockLottery?: boolean;
  dailyPlayLimit?: number;
  dailyPlaysRemaining?: number;
  totalPlayLimit?: number;
  totalPlaysRemaining?: number;
  newCustomerOnlyDays?: number;
  newCustomerEligible?: boolean | null;
  newCustomerDaysRemaining?: number | null;
  paidAmountRequirementScope?: 'cumulative' | 'monthly' | null;
  paidAmountRequirementMinimum?: number | null;
  paidAmountRequirementLabel?: string | null;
  paidAmountRequirementCurrentAmount?: number | null;
  paidAmountRequirementEligible?: boolean | null;
  customerListRestricted?: boolean;
  customerListEligible?: boolean | null;
  startsAt?: string | null;
  endsAt?: string | null;
};

const EventCard: React.FC<EventCardProps> = ({ id, uid, siteId, siteSlug, name, description, imageUrl, imageVariants, lotteryPoolsAvailableCount, lotteryPoolsCount, status, status_label, lotteryAnimationUrl, prizeImages, prizeImageVariantUrls, gachaCost = 400, useProductStockLottery = true, dailyPlayLimit, dailyPlaysRemaining, totalPlayLimit, totalPlaysRemaining, newCustomerOnlyDays, newCustomerEligible, newCustomerDaysRemaining, paidAmountRequirementScope, paidAmountRequirementMinimum, paidAmountRequirementEligible, customerListRestricted = false, customerListEligible, startsAt, endsAt }) => {
  const { currentUser } = useAuth();
  const {
    handleGachaClick,
    executeGacha,
    onAnimationEnd,
    skipAllAnimations,
    isConfirmDialogOpen,
    isLoginDialogOpen,
    isPointDialogOpen,
    isAnimationPlaying,
    setIsConfirmDialogOpen,
    setIsLoginDialogOpen,
    setIsPointDialogOpen,
    pendingGacha,
    currentAnimationUrl,
    currentAnimationIndex,
    drawResults,
    isPhoneVerificationRequired,
    setIsPhoneVerificationRequired,
  } = useGacha();

  // Calculate progress metrics
  const hideRemainingStock = lotteryPoolsAvailableCount == null;
  const effectiveAvailable = lotteryPoolsAvailableCount ?? 0;
  const progressPercentage = calculateProgress(effectiveAvailable, lotteryPoolsCount);
  const progressColor = getProgressColor(progressPercentage, effectiveAvailable);
  const progressLabel = getProgressLabel(effectiveAvailable, lotteryPoolsCount);
  const soldOut = isSoldOut(effectiveAvailable);

  // 開催期間の判定（くじカードと同じロジック。未設定なら常時開催扱い）
  const now = new Date();
  const startsAtDate = startsAt ? new Date(startsAt) : null;
  const endsAtDate = endsAt ? new Date(endsAt) : null;
  const isBeforePeriod = startsAtDate !== null && now < startsAtDate;
  const isAfterPeriod = endsAtDate !== null && now > endsAtDate;

  // イベント状態判定（期間を加味）
  const isEventFinished = status === 'finished' || status === 'cancelled' || isAfterPeriod;
  const isEventReady = status === 'ready' || isBeforePeriod;
  const isEventActive = (status === 'active' || status === 'published') && !isBeforePeriod && !isAfterPeriod;
  const isLegacyEvent = !useProductStockLottery;
  const isDailyLimitEvent = dailyPlayLimit != null && dailyPlayLimit > 0;
  const dailyLimitReached = isDailyLimitEvent && dailyPlaysRemaining != null && dailyPlaysRemaining <= 0;
  const isTotalLimitEvent = totalPlayLimit != null && totalPlayLimit > 0;
  const totalLimitReached = isTotalLimitEvent && totalPlaysRemaining != null && totalPlaysRemaining <= 0;
  const isNewCustomerOnlyEvent = newCustomerOnlyDays != null && newCustomerOnlyDays > 0;
  const newCustomerLimitReached = isNewCustomerOnlyEvent && currentUser != null && newCustomerEligible === false;
  const showNewCustomerDaysRemaining = isNewCustomerOnlyEvent && newCustomerEligible !== false && newCustomerDaysRemaining != null;
  const paidAmountRequirementEnabled = paidAmountRequirementScope != null && paidAmountRequirementMinimum != null && paidAmountRequirementMinimum > 0;
  const paidAmountRequirementBlocked = paidAmountRequirementEnabled && paidAmountRequirementEligible === false;
  const customerListBlocked = customerListRestricted && currentUser != null && customerListEligible === false;
  const formatAmount = (amount?: number | null) => (amount ?? 0).toLocaleString('ja-JP');
  const vipRequirementScopeLabel = paidAmountRequirementScope === 'monthly' ? '当月ポイントチャージ実績' : '累計ポイントチャージ実績';
  const vipRequirementConditionLabel = `${vipRequirementScopeLabel}${formatAmount(paidAmountRequirementMinimum)}pt以上`;
  const participationConditionEnabled = paidAmountRequirementEnabled || customerListRestricted;
  const vipRequirementBlockedLabel = `参加条件：${vipRequirementConditionLabel}`;
  const restrictionMessage = newCustomerLimitReached
    ? `参加対象は新規登録から${newCustomerOnlyDays}日以内です`
    : totalLimitReached
    ? `通算${totalPlayLimit}回の上限に達しました`
    : paidAmountRequirementBlocked
    ? vipRequirementBlockedLabel
    : customerListBlocked
    ? '参加条件を満たしていません'
    : dailyLimitReached
    ? '✅ 本日完了'
    : null;
  const canPlay = !soldOut && !isEventFinished && isEventActive && !isLegacyEvent && !dailyLimitReached && !totalLimitReached && !newCustomerLimitReached && !paidAmountRequirementBlocked && !customerListBlocked;
  const canPlayCount = (count: number) => canPlay && (!isTotalLimitEvent || totalPlaysRemaining == null || totalPlaysRemaining >= count);

  // 開催期間の表示（くじカードに合わせた YYYY年MM月DD日 (曜) HH:MM 形式）
  const formatPeriodDate = (iso: string): string => {
    const d = new Date(iso);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const weekday = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
    return `${year}年${month}月${day}日 (${weekday}) ${hours}:${minutes}`;
  };
  // 開催前は開始日時「◯◯ 開催」、開催中は終了日時「◯◯ まで」を表示。
  // 終了後はオーバーレイ／メッセージで伝えるため日付は出さない。未設定も非表示。
  const periodLabel = isAfterPeriod
    ? null
    : isBeforePeriod && startsAt
    ? `${formatPeriodDate(startsAt)} 開催`
    : endsAt
    ? `${formatPeriodDate(endsAt)} まで`
    : null;

  // リンク先を決定
  const eventLink = `/oripa/${uid}`;

  return (
    <>
      <Link to={eventLink} className="group relative block">
        <div className="bg-white border-4 border-black rounded-xl overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
          {/* メイン画像 */}
          <div className="relative aspect-[800/525] bg-gray-100">
            <ResponsiveImage
              src={imageUrl}
              variants={imageVariants}
              alt={name}
              displaySize="card"
              className={`w-full h-full object-contain transition duration-500 ${
                soldOut || isEventFinished || isEventReady
                  ? 'grayscale opacity-60'
                  : ''
              }`}
            />
            {/* ステータスバッジ */}
            <div className="absolute top-3 right-3 flex max-w-[calc(100%-1.5rem)] flex-wrap items-start justify-end gap-1.5">
              {isNewCustomerOnlyEvent && (
                <span className="inline-flex shrink-0 items-center whitespace-nowrap px-2 py-1 border-2 border-black rounded-lg text-[11px] font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] bg-pink-400 text-black" aria-label={`新規登録から${newCustomerOnlyDays}日以内限定`}>
                  新規登録ユーザー限定
                </span>
              )}
              {dailyPlayLimit && dailyPlayLimit > 0 && (
                <span className="inline-flex shrink-0 items-center whitespace-nowrap px-2 py-1 border-2 border-black rounded-lg text-[11px] font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] bg-yellow-400 text-black" aria-label={`1日${dailyPlayLimit}回限定`}>
                  🎁 1日{dailyPlayLimit}回
                </span>
              )}
              {isTotalLimitEvent && (
                <span className="inline-flex shrink-0 items-center whitespace-nowrap px-2 py-1 border-2 border-black rounded-lg text-[11px] font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] bg-blue-300 text-black" aria-label={`通算${totalPlayLimit}回限定`}>
                  通算{totalPlayLimit}回限定
                </span>
              )}
              {participationConditionEnabled && (
                <span className="inline-flex shrink-0 items-center whitespace-nowrap px-2 py-1 border-2 border-black rounded-lg text-[11px] font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] bg-orange-300 text-black">
                  参加条件あり
                </span>
              )}
              {status && status !== 'active' && (
                <span className={`inline-flex items-center px-3 py-1.5 border-2 border-black rounded-lg text-xs font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] ${
                  status === 'ready' ? 'bg-blue-400 text-black' :
                  status === 'finished' ? 'bg-purple-400 text-black' :
                  status === 'cancelled' ? 'bg-red-400 text-black' :
                  'bg-gray-400 text-black'
                }`}>
                  {status === 'ready' ? '開催予定' : (status_label || status)}
                </span>
              )}
            </div>
            {(soldOut || isEventFinished || isEventReady) && (
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                <div className="bg-white border-4 border-black px-6 py-3 rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <span className="text-lg font-black text-black">
                    {isEventReady ? '開催予定' : isEventFinished ? 'イベント終了' : '受付終了'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* 当選商品サムネイル */}
          {prizeImages && prizeImages.length > 0 && (
            <div className="flex border-t-4 border-black bg-gray-100">
              {[0, 1, 2, 3, 4].map((idx) => {
                const img = prizeImages[idx];
                return (
                  <div
                    key={idx}
                    className={`flex-1 aspect-[63/88] bg-white ${idx < 4 ? 'border-r-2 border-black' : ''}`}
                  >
                    {img && (
                      <ResponsiveImage
                        src={img}
                        variants={prizeImageVariantUrls?.[idx]}
                        alt={`当選商品 ${idx + 1}`}
                        displaySize="thumbnail"
                        loading="lazy"
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* コンテンツエリア */}
          <div className="relative border-t-4 border-black bg-white px-4 pt-3 pb-4">
            {soldOut || isEventFinished || isEventReady || isLegacyEvent ? (
              // 完売時・終了時・開催予定・旧方式イベントの表示
              <div className="py-4 text-center">
                <p className={`text-lg font-black ${isEventReady ? 'text-blue-600' : 'text-gray-500'}`}>
                  {isEventReady ? '開催予定' : isLegacyEvent ? '現在ご利用いただけません' : '終了しました'}
                </p>
              </div>
            ) : (
              // 通常時の表示
              <>
                <div className="flex justify-between items-center mb-2">
                  <span className="whitespace-nowrap bg-theme-primary text-theme-button px-3 py-1 text-xs font-black border-2 border-black rounded-lg">
                    {gachaCost} pt
                  </span>
                  <span className="text-xs font-bold text-gray-600">
                    {isEventReady ? '開催予定' : customerListBlocked ? '対象者限定' : progressLabel}
                  </span>
                </div>

                <div className="mt-2">
                  <span id={`ProgressLabel-${uid}`} className="sr-only">Progress</span>
                  <span
                    role="progressbar"
                    aria-labelledby={`ProgressLabel-${uid}`}
                    aria-valuenow={progressPercentage}
                    className="block rounded-lg bg-gray-200 border-2 border-black overflow-hidden"
                  >
                    <span
                      className={`block h-4 text-center text-[10px]/4 font-bold transition-all duration-500 ${progressColor}`}
                      style={{ width: `${Math.max(progressPercentage, 8)}%` }}
                    >
                      <span className="text-white">
                        {progressPercentage}%
                      </span>
                    </span>
                  </span>
                </div>

                <p className="mt-1.5 text-sm font-bold text-gray-700">のこり {lotteryPoolsAvailableCount} / {lotteryPoolsCount} 回</p>

                {isTotalLimitEvent && (
                  <p className="mt-1 text-xs font-bold text-blue-700">
                    お一人様通算{totalPlayLimit}回まで{currentUser && totalPlaysRemaining != null ? `（残り${totalPlaysRemaining}回）` : ''}
                  </p>
                )}

                {dailyPlayLimit != null && dailyPlayLimit > 0 ? (
                <div className="mt-3">
                  {restrictionMessage ? (
                    <div className={`block w-full rounded-lg px-2 py-2 sm:py-3 text-xs sm:text-sm font-black border-2 text-center ${
                      dailyLimitReached && !newCustomerLimitReached && !totalLimitReached && !paidAmountRequirementBlocked && !customerListBlocked
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : 'bg-amber-50 border-amber-300 text-amber-800'
                    }`}>
                      {restrictionMessage}
                    </div>
                  ) : (
                    <button
                      className={`block w-full rounded-lg px-2 py-2 sm:py-3 text-xs sm:text-sm font-black border-2 transition-all ${
                        !canPlayCount(1)
                          ? 'bg-gray-300 border-gray-400 text-gray-500 cursor-not-allowed'
                          : 'bg-theme-primary border-black text-theme-button shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (canPlayCount(1)) handleGachaClick(e, 'single', siteId, uid, gachaCost, 0);
                      }}
                      disabled={!canPlayCount(1)}
                    >
                      <span className="hidden sm:inline">1回ガチャ</span>
                      <span className="sm:hidden">1回</span>
                    </button>
                  )}
                </div>
                ) : (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {restrictionMessage && (
                    <div className="col-span-3 rounded-lg px-2 py-2 text-xs font-black border-2 bg-amber-50 border-amber-300 text-amber-800 text-center">
                      {restrictionMessage}
                    </div>
                  )}
                  <button
                    className={`block w-full rounded-lg px-2 py-2 sm:py-3 text-xs sm:text-sm font-black border-2 transition-all ${
                      !canPlayCount(1)
                        ? 'bg-gray-300 border-gray-400 text-gray-500 cursor-not-allowed'
                        : 'bg-white border-black text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (canPlayCount(1)) handleGachaClick(e, 'single', siteId, uid, gachaCost, 0);
                    }}
                    disabled={!canPlayCount(1)}
                  >
                    <span className="hidden sm:inline">1回ガチャ</span>
                    <span className="sm:hidden">1回</span>
                  </button>
                  <button
                    className={`block w-full rounded-lg px-2 py-2 sm:py-3 text-xs sm:text-sm font-black border-2 transition-all ${
                      !canPlayCount(10)
                        ? 'bg-gray-300 border-gray-400 text-gray-500 cursor-not-allowed'
                        : 'bg-theme-primary border-black text-theme-button shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (canPlayCount(10)) handleGachaClick(e, 'multi10', siteId, uid, gachaCost, 0);
                    }}
                    disabled={!canPlayCount(10)}
                  >
                    <span className="hidden sm:inline">10連ガチャ</span>
                    <span className="sm:hidden">10連</span>
                  </button>
                  <button
                    className={`block w-full rounded-lg px-2 py-2 sm:py-3 text-xs sm:text-sm font-black border-2 transition-all ${
                      !canPlayCount(100)
                        ? 'bg-gray-300 border-gray-400 text-gray-500 cursor-not-allowed'
                        : 'bg-pink-400 border-black text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (canPlayCount(100)) handleGachaClick(e, 'multi100', siteId, uid, gachaCost, 0);
                    }}
                    disabled={!canPlayCount(100)}
                  >
                    <span className="hidden sm:inline">100連ガチャ</span>
                    <span className="sm:hidden">100連</span>
                  </button>
                </div>
                )}
              </>
            )}

            {/* 開催期間（くじカード準拠：YYYY年MM月DD日 (曜) HH:MM 形式・グレー） */}
            {periodLabel && (
              <p className="mt-2 text-sm text-gray-500">{periodLabel}</p>
            )}
          </div>
        </div>
      </Link>

      {/* 確認ダイアログ */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ガチャを実行しますか？</DialogTitle>
            <DialogDescription>
              {pendingGacha?.cost} ポイント消費して{pendingGacha?.count}回引きます
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button onClick={executeGacha}>
              実行する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 未ログインダイアログ */}
      <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ログインしてください</DialogTitle>
            <DialogDescription>
              ガチャを回すためにはログインが必要です。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsLoginDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button asChild>
              <Link to="/login">ログインページへ</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ポイント不足ダイアログ */}
      <Dialog open={isPointDialogOpen} onOpenChange={setIsPointDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ポイントが不足しています</DialogTitle>
            <DialogDescription>
              ガチャを回すためにはポイントが必要です。
              <br />
              ポイントはポイント購入ページでチャージできます。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsPointDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button asChild>
              <Link to="/point">オリパポイントを購入</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 抽選アニメーション */}
      <LotteryAnimation
        animationUrl={currentAnimationUrl || lotteryAnimationUrl}
        isPlaying={isAnimationPlaying}
        onAnimationEnd={onAnimationEnd}
        onSkipAll={skipAllAnimations}
        gachaCount={pendingGacha?.count || 1}
        currentIndex={currentAnimationIndex}
        totalCount={drawResults.length}
      />

      {/* 電話番号認証ダイアログ */}
      <PhoneVerificationDialog
        isOpen={isPhoneVerificationRequired}
        onClose={() => setIsPhoneVerificationRequired(false)}
        onSuccess={() => {
          setIsPhoneVerificationRequired(false);
        }}
      />
    </>
  );
};

export default EventCard;
